import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Upload, Check, AlertCircle, Building2, DollarSign, MapPin, Zap, Crown, Star, Infinity, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBalance } from '../contexts/BalanceContext';
import { extractTextFromFile } from '../utils/fileHandlers';
import { generateCoverLetter } from '../utils/openai';
import { FilterState } from '../components/Filters';
import { uploadFileToStorage } from '../utils/firebase/storage';
import { saveGeneration } from '../utils/firebase/generations';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../utils/firebase/config';
import { collection, getDocs, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase/config';
import { startCheckoutSession } from '../components/pages/Pricing';
import {
  trackFunnelStep,
  trackDocumentGeneration,
  trackAuthentication,
  trackUserAction,
  trackFunnelStep4
} from '../utils/analytics';

// Step components
import SignupStep from '../components/funnel/SignupStep';
import ResumeStep from '../components/funnel/ResumeStep';
import JobDescriptionStep from '../components/funnel/JobDescriptionStep';

const functions = getFunctions(app);

interface FunnelState {
  currentStep: number;
  resumeContent: string;
  fileName: string;
  jobDescription: string;
  filters: FilterState;
  generatedLetter: string;
  jobRecommendations: {
    jobTitle: string;
    count: number;
    urls: Record<string, string>;
  } | null;
  showPricing: boolean;
}

interface StartFunnelProps {
  initialStep?: number;
}

interface Product {
  price: ProductPrice;
  active: boolean;
  description: string;
  id: string;
  images: string[];
  metadata: Metadata;
  name: string;
  popular: boolean;
}

interface Metadata {
  popular?: 0 | 1;
  oldPrice?: string;
  [key: string]: string | number | undefined;
}

interface ProductPrice {
  id: string;
  tax_behavior: string;
  interval_count: number;
  billing_scheme: string;
  tiers: null;
  product: string;
  description: null;
  metadata: Metadata;
  type: string;
  trial_period_days: null;
  tiers_mode: null;
  transform_quantity: null;
  active: boolean;
  recurring: {
    interval: string;
    interval_count: 1;
    aggregate_usage: null;
    usage_type: string;
    meter: null;
    trial_period_days: null;
  };
  unit_amount: number;
  interval: string;
  currency: string;
}

interface Plan {
  title: string;
  description: string;
  price: string;
  duration: string;
  features: string[];
  isLimitedOffer?: boolean;
  stripeButtonId: string;
  priceId: string;
}

function toPlan(p: Product): Plan | null {
  if (!p.price || !p.price.active) {
    return null;
  }

  const features = Object.entries(p.metadata)
    .filter(([key]) => key.startsWith('feat'))
    .sort(([a], [b]) => {
      const numA = parseInt(a.replace('feat', ''));
      const numB = parseInt(b.replace('feat', ''));
      return numA - numB;
    })
    .map(([_, value]) => value)
    .filter((v): v is string => typeof v === 'string' && Boolean(v));

  return {
    title: p.name,
    price: (p.price.unit_amount / 100).toString(),
    description: p.description,
    features,
    isLimitedOffer: Boolean(p.metadata.popular),
    stripeButtonId: p.id,
    priceId: p.price.id,
    duration: p.price.recurring.interval,
  };
}

export default function StartFunnel({ initialStep = 1 }: StartFunnelProps) {
  const { user } = useAuth();
  const { balance } = useBalance();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  const [state, setState] = useState<FunnelState>({
    currentStep: initialStep,
    resumeContent: '',
    fileName: '',
    jobDescription: '',
    filters: {
      length: 'short',
      styles: ['casual'],
      purposes: ['job']
    },
    generatedLetter: '',
    jobRecommendations: null,
    showPricing: false
  });
  
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [salary, setSalary] = useState<number>(0);
  const [experience, setExperience] = useState<number>(0);

  useEffect(() => {
    if (showResults) {
      trackFunnelStep4.viewApplicationKit();
    }
  }, [showResults]);

  const handleUnlockResume = () => {
    trackFunnelStep4.clickUnlockResume();
    handleShowPricing();
  };

  const handleUnlockJobMatches = () => {
    trackFunnelStep4.clickUnlockJobMatches();
    handleShowPricing();
  };

  useEffect(() => {
    const loadPrices = async () => {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, 'products'),
            where('active', '==', true)
          )
        );

        const productsWithPrices = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const priceSnap = await getDocs(
              collection(db, 'products', doc.id, 'prices')
            );
            const activePrices = priceSnap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((it) => (it as ProductPrice).active);
            
            const price = activePrices[0] as ProductPrice;
            
            if (!price) {
              return null;
            }

            return {
              ...doc.data(),
              price,
              active: true,
            };
          })
        );

        const validProducts = productsWithPrices.filter(Boolean) as Product[];
        const validPlans = validProducts
          .map((it) => toPlan(it))
          .filter((plan): plan is Plan => plan !== null)
          .filter((plan) => [
            'prod_SOFFCXdUskOzjH',
          ].includes(plan.stripeButtonId));

        const sortedPlans = validPlans.sort((a, b) => {
          if (a.isLimitedOffer && !b.isLimitedOffer) return 1;
          if (!a.isLimitedOffer && b.isLimitedOffer) return -1;
          return 0;
        });

        setPlans(sortedPlans);
      } catch (error) {
        console.error('Error loading prices:', error);
      }
    };

    if (state.showPricing) {
      loadPrices();
    }
  }, [state.showPricing]);

  const handleStartCheckout = async (priceId: string) => {
    if (isProcessing) return;
    setIsProcessing(true);

    // Если это наш разовый товар — всегда payment
    const isOneTime = priceId === '1RTN1NGKwh1I2qoYtpT3UJrA';
    const mode: 'payment' | 'subscription' = isOneTime ? 'payment' : 'subscription';

    try {
      await startCheckoutSession(
        priceId,
        mode,
        () => {},
        (docRef: any) => {
          onSnapshot(docRef, (snap: any) => {
            const { error, url } = snap.data() as any;
            if (error) {
              alert(`An error occurred: ${error.message}`);
              setIsProcessing(false);
            }
            if (url) {
              window.location.assign(url);
              setIsProcessing(false);
            }
          });
        },
        (error: any) => {
          setIsProcessing(false);
          console.error('Failed to start checkout:', error);
        }
      );
    } catch (error) {
      setIsProcessing(false);
      console.error('Failed to start checkout:', error);
    }
  };

  useEffect(() => {
    if (user && state.currentStep === 1) {
      handleNextStep();
    }
  }, [user, state.currentStep]);

  useEffect(() => {
    if (showResults && state.jobDescription) {
      generateJobRecommendations();
    }
  }, [showResults, state.jobDescription]);
  
  const handleNextStep = () => {
    if (validateCurrentStep()) {
      trackFunnelStep(state.currentStep, `Step ${state.currentStep}`, {
        success: true
      });
      setState(prev => ({
        ...prev,
        currentStep: prev.currentStep + 1
      }));
      window.scrollTo(0, 0);
    } else {
      trackFunnelStep(state.currentStep, `Step ${state.currentStep}`, {
        success: false,
        error: error
      });
    }
  };
  
  const validateCurrentStep = (): boolean => {
    setError('');
    
    switch (state.currentStep) {
      case 1:
        return true;
        
      case 2:
        if (!state.resumeContent) {
          setError('Please upload a resume or paste resume text');
          return false;
        }
        return true;
        
      case 3:
        if (!state.jobDescription) {
          setError('Please enter a job description');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };
  
  const handleFileUpload = async (file: File) => {
    setError('');
    setUploadProgress(0);
    setIsLoading(true);
    
    try {
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File is too large. Please upload a smaller file (max 10MB).');
      }
      
      setUploadProgress(25);

      if (user) {
        await uploadFileToStorage(file);
      }

      const content = await extractTextFromFile(file);
      setUploadProgress(75);
      
      if (typeof content === 'string' && content.startsWith('Error:')) {
        throw new Error(content);
      }

      trackUserAction('file_upload', 'resume', file.name, file.size);
      
      setState(prev => ({
        ...prev,
        resumeContent: content as string,
        fileName: file.name
      }));
      
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 1000);
      setIsDropdownOpen(false);
    } catch (error) {
      trackUserAction('file_upload_error', 'resume', error.message);
      setError(error instanceof Error ? error.message : 'Error processing file');
      setUploadProgress(0);
      setState(prev => ({
        ...prev,
        fileName: '',
        resumeContent: ''
      }));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResumeTextChange = (text: string) => {
    setState(prev => ({
      ...prev,
      resumeContent: text
    }));
  };
  
  const handleJobDescriptionChange = (text: string) => {
    setState(prev => ({
      ...prev,
      jobDescription: text
    }));
  };

  const generateJobRecommendations = async () => {
    try {
      const generateJobRecommendationsFunction = httpsCallable(functions, 'generateJobRecommendations');
      const result = await generateJobRecommendationsFunction({
        jobDescription: state.jobDescription
      });

      setState(prev => ({
        ...prev,
        jobRecommendations: result.data as any
      }));
    } catch (error) {
      console.error('Error generating job recommendations:', error);
    }
  };
  
  const handleGenerateCoverLetter = async () => {
    if (!validateCurrentStep()) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await generateCoverLetter({
        jobDescription: state.jobDescription,
        resumeContent: state.resumeContent,
        options: { filters: state.filters }
      });
      
      if (result.data) {
        trackDocumentGeneration('cover_letter', true);
        
        const generationId = await saveGeneration(
          result.data,
          state.jobDescription,
          state.filters
        );
        
        setState(prev => ({
          ...prev,
          generatedLetter: result.data
        }));
        
        setShowResults(true);
      } else {
        throw new Error('Failed to generate letter content');
      }
    } catch (error: any) {
      trackDocumentGeneration('cover_letter', false, error.message);
      console.error('Generation error:', error);
      setError(error.message || 'Error generating cover letter');
    } finally {
      setIsLoading(false);
    }
  };

  const generateSalary = (title: string) => {
    const baseRanges = {
      'junior': { min: 60000, max: 90000 },
      'senior': { min: 120000, max: 180000 },
      'lead': { min: 150000, max: 220000 },
      'default': { min: 80000, max: 140000 }
    };

    let range = baseRanges.default;
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes('senior') || lowerTitle.includes('sr.')) {
      range = baseRanges.senior;
    } else if (lowerTitle.includes('junior') || lowerTitle.includes('jr.')) {
      range = baseRanges.junior;
    } else if (lowerTitle.includes('lead') || lowerTitle.includes('head')) {
      range = baseRanges.lead;
    }

    const min = Math.floor(range.min / 1000) * 1000;
    const max = Math.floor(range.max / 1000) * 1000;
    return `$${(min/1000).toFixed(0)}k - $${(max/1000).toFixed(0)}k`;
  };

  const companies = [
    { name: 'TechCorp Solutions', location: 'San Francisco, CA' },
    { name: 'InnovateSoft Inc.', location: 'New York, NY' },
    { name: 'Digital Dynamics', location: 'Austin, TX' },
    { name: 'Future Systems', location: 'Seattle, WA' },
    { name: 'CodeCraft Technologies', location: 'Boston, MA' }
  ];

  const generateMatchPercentage = (index: number) => {
    const basePercentages = [98, 95, 92];
    return basePercentages[index] || 90;
  };

  const handleShowPricing = () => {
    setState(prev => ({
      ...prev,
      showPricing: true
    }));
  };
  
  const progressPercentage = (state.currentStep / 3) * 100;

  if (state.showPricing) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold mb-4">Unlock Premium Features</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get unlimited access to all features and maximize your job search success
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {plans.map((plan) => (
              <div 
                key={plan.priceId}
                className={`bg-white rounded-xl shadow-lg p-8 border-2 border-transparent hover:border-primary transition-all duration-300 ${
                  plan.isLimitedOffer ? 'relative overflow-hidden' : ''
                }`}
              >
                {plan.isLimitedOffer && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-success/10 text-success px-3 py-1 rounded-full text-sm">
                      Save 40%
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">{plan.title}</h2>
                  {!plan.isLimitedOffer && (
                    <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                      Popular
                    </span>
                  )}
                </div>
                <div className="mb-6">
                  <div className="flex items-end mb-2">
                    <span className="text-4xl font-bold">${plan.price}</span>
                    <span className="text-gray-500 ml-2">/{plan.duration}</span>
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <Check className="w-5 h-5 text-green-500 mr-3" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => handleStartCheckout(plan.priceId)}
                  disabled={isProcessing}
                  className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50"
                >
                  {isProcessing ? 'Processing...' : 'Get Started'}
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => setState(prev => ({ ...prev, showPricing: false }))}
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              Return to Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-center mb-8">Here is your Application Kit!</h1>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex flex-col">
                <h2 className="text-xl font-semibold mb-2">Job-specific cover letter</h2>
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                  <p className="text-sm text-primary">
                    Every second HR reads the cover letter from Hiiroo all the way to the end
                  </p>
                </div>
              </div>
              <div className="prose max-w-none">
                {state.generatedLetter.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 text-gray-700 leading-relaxed whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col">
              <div className="flex flex-col">
                <h2 className="text-xl font-semibold mb-2">Job-specific resume</h2>
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                  <p className="text-sm text-primary">
                    9 out of 10 customized resumes made by Hiiroo pass ATS and get the attention of HR
                  </p>
                </div>
              </div>
              <div className="relative overflow-hidden flex-1" style={{ height: '400px' }}>
                <div className="px-4" style={{ filter: 'blur(4px)' }}>
                  <div className="text-center mb-6">
                    <h1 className="text-2xl font-bold mb-2">John Smith</h1>
                    <p className="text-gray-600">
                      Senior Software Engineer • San Francisco, CA • john@example.com • (555) 123-4567
                    </p>
                  </div>

                  <div className="mb-6">
                    <h2 className="text-xl font-bold border-b-2 border-gray-200 mb-4">Professional Summary</h2>
                    <p className="text-gray-700 mb-4">
                      Results-driven software engineer with 8+ years of experience in full-stack development. 
                      Proven track record of delivering scalable solutions and leading high-performance teams.
                    </p>
                  </div>

                  <div className="mb-6">
                    <h2 className="text-xl font-bold border-b-2 border-gray-200 mb-4">Experience</h2>
                    <div className="mb-4">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className="font-semibold">Senior Software Engineer</h3>
                        <span className="text-gray-600">2020 - Present</span>
                      </div>
                      <div className="text-gray-600 mb-2">Tech Solutions Inc.</div>
                      <ul className="list-disc ml-4 space-y-1">
                        <li className="text-gray-700">Led development of microservices architecture</li>
                        <li className="text-gray-700">Improved system performance by 40%</li>
                        <li className="text-gray-700">Mentored junior developers</li>
                      </ul>
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold border-b-2 border-gray-200 mb-4">Skills</h2>
                    <div className="flex flex-wrap gap-2">
                      {['JavaScript', 'React', 'Node.js', 'Python', 'AWS', 'Docker'].map((skill) => (
                        <span key={skill} className="bg-gray-100 px-3 py-1 rounded-full text-gray-700">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div 
                  className="absolute bottom-0 left-0 right-0 h-40"
                  style={{
                    background: 'linear-gradient(to bottom, transparent, white)',
                    pointerEvents: 'none'
                  }}
                />
              </div>
              
              <button
                onClick={handleUnlockResume}
                className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary-hover transition-colors mt-6"
              >
                Unlock Full Resume
              </button>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col">
              <div className="flex flex-col">
                <h2 className="text-xl font-semibold mb-2">AI-Personalized Job Finder</h2>
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
                  <p className="text-sm text-primary">
                    These jobs have an 87% or higher chance of getting an interview given your kit
                  </p>
                </div>
              </div>
              <div className="space-y-4 flex-1">
                {[1, 2, 3].map((_, index) => {
                  const company = companies[Math.floor(Math.random() * companies.length)];
                  const jobTitle = state.jobRecommendations?.jobTitle || "Software Engineer";
                  const matchPercentage = generateMatchPercentage(index);
                  return (
                    <div key={index} className="p-4 border border-gray-100 rounded-lg hover:shadow-md transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Zap className={`w-4 h-4 ${matchPercentage >= 95 ? 'text-primary' : 'text-primary'}`} />
                          <span className={`text-sm font-medium ${matchPercentage >= 95 ? 'text-primary' : 'text-primary'}`}>
                            {matchPercentage}% Match
                          </span>
                        </div>
                        <span className="text-success font-medium">{generateSalary(jobTitle)}</span>
                      </div>
                      <h3 className="font-medium text-lg mb-2">{jobTitle}</h3>
                      <div className="space-y-2 text-gray-600">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>{company.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{company.location}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <button
                onClick={handleUnlockJobMatches}
                className="w-full bg-primary text-white py-3 rounded-lg hover:bg-primary-hover transition-colors mt-6"
              >
                Unlock All Job Matches
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto mb-8">
        <div className="relative pt-1">
          <div className="flex mb-2 items-center justify-between">
            <div>
              <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-primary bg-primary/10">
                Step {state.currentStep} of 3
              </span>
            </div>
            <div className="text-right">
              <span className="text-xs font-semibold inline-block text-primary">
                {Math.round(progressPercentage)}%
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-primary/10">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.5 }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary"
            />
          </div>
          <div className="flex justify-between">
            {[1, 2, 3].map((step) => (
              <div 
                key={step}
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  step <= state.currentStep ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step < state.currentStep ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <span>{step}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-3xl mx-auto mb-6">
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          {state.currentStep === 1 && (
            <SignupStep onContinue={handleNextStep} />
          )}
          {state.currentStep === 2 && (
            <ResumeStep
              fileName={state.fileName}
              resumeContent={state.resumeContent}
              uploadProgress={uploadProgress}
              isLoading={isLoading}
              onFileUpload={handleFileUpload}
              onContinue={handleNextStep}
              salary={salary}
              setSalary={setSalary}
              experience={experience}
              setExperience={setExperience}
            />
          )}
          {state.currentStep === 3 && (
            <JobDescriptionStep
              jobDescription={state.jobDescription}
              filters={state.filters}
              isLoading={isLoading}
              onJobDescriptionChange={handleJobDescriptionChange}
              onFilterChange={() => {}}
              onGenerate={handleGenerateCoverLetter}
            />
          )}
        </div>
      </div>
    </div>
  );
}